<script setup lang="ts">
import { reactive, provide, computed, ref, onMounted, onUnmounted, watch } from 'vue'
import { expandTemplate } from '../templateEngine'
import type { AdaptiveCardSchema, Action, Element } from '../types'
import { CARD_CONTEXT_KEYS } from '../composables/useCardContext'
import ACElement from './ACElement.vue'
import ACActionList from './ACActionList.vue'

const props = withDefaults(defineProps<{
  template: AdaptiveCardSchema
  data?: Record<string, unknown>
  enabled?: boolean
  onAction?: (action: Action, inputs: Record<string, string>) => void
  prefillValues?: Record<string, string>
}>(), {
  enabled: true,
})

const inputs = reactive<Record<string, string>>({})
const errors = reactive<Record<string, string>>({})
const visibility = reactive<Record<string, boolean>>({})
const isEnabled = ref(props.enabled !== false)
const cardRef = ref<HTMLElement>()
const cardWidth = ref(800)
const cardInstanceId = `ac-${(globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`).replace(/[^a-zA-Z0-9_-]/g, '')}`

const expandedCard = computed<AdaptiveCardSchema>(() =>
  expandTemplate(props.template, props.data ?? {}) as AdaptiveCardSchema
)

// Seed initial form input state from prefillValues, separate from template data.
// Runs after expandedCard so the intent is clear: template is built first, then inputs are set.
watch(
  () => props.prefillValues,
  (values) => {
    if (values && Object.keys(values).length > 0) {
      Object.assign(inputs, values)
    }
  },
  { immediate: true },
)

function findElementById(elements: Element[], id: string): Element | undefined {
  for (const el of elements) {
    if ((el as { id?: string }).id === id) return el
    if ('items' in el && Array.isArray(el.items)) {
      const found = findElementById(el.items as Element[], id)
      if (found) return found
    }
    if ('columns' in el && Array.isArray(el.columns)) {
      for (const col of el.columns) {
        if ('items' in col && Array.isArray(col.items)) {
          const found = findElementById(col.items as Element[], id)
          if (found) return found
        }
      }
    }
  }
  return undefined
}

function toggleVisibility(elementId: string, isVisible?: boolean) {
  if (isVisible !== undefined) {
    visibility[elementId] = isVisible
    return
  }
  
  // If already toggled, just flip the state
  if (elementId in visibility) {
    visibility[elementId] = !visibility[elementId]
    return
  }
  
  // First toggle - check element's initial isVisible property
  const element = findElementById(expandedCard.value.body, elementId)
  const initialVisible = (element as { isVisible?: boolean })?.isVisible !== false
  visibility[elementId] = !initialVisible
}

let resizeObserver: ResizeObserver | null = null

onMounted(() => {
  if (cardRef.value) {
    cardWidth.value = cardRef.value.offsetWidth
    resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        cardWidth.value = entry.contentRect.width
      }
    })
    resizeObserver.observe(cardRef.value)
  }
  isEnabled.value = props.enabled !== false
})

onUnmounted(() => {
  resizeObserver?.disconnect()
})

provide(CARD_CONTEXT_KEYS.inputs, inputs)
provide(CARD_CONTEXT_KEYS.errors, errors)
provide(CARD_CONTEXT_KEYS.elements, computed(() => expandedCard.value.body))
provide(CARD_CONTEXT_KEYS.cardWidth, cardWidth)
provide(CARD_CONTEXT_KEYS.cardInstanceId, cardInstanceId)
provide(CARD_CONTEXT_KEYS.visibility, visibility)
provide(CARD_CONTEXT_KEYS.toggleVisibility, toggleVisibility)
provide(CARD_CONTEXT_KEYS.isEnabled, isEnabled)
provide(CARD_CONTEXT_KEYS.setEnabled, (value: boolean) => {
  isEnabled.value = value
})
watch(
  () => props.enabled,
  (next) => {
    isEnabled.value = next !== false
  },
)
</script>

<template>
  <div ref="cardRef" class="adaptive-card [&>*:first-child]:mt-0">
    <ACElement
      v-for="(element, index) in expandedCard.body"
      :key="index"
      :element="element"
      :on-action="props.onAction"
    />
    <ACActionList
      v-if="expandedCard.actions?.length"
      :actions="expandedCard.actions"
      :on-action="props.onAction"
      :elements="expandedCard.body"
      class="mt-4"
    />
  </div>
</template>
